import React, { useMemo, useState } from "react";
import { Space, Tag, Typography, Dropdown, Button, Modal } from "antd";
import { DownOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import AssessmentStatusFilter, {
  RoundStatusFilter,
} from "./AssessmentStatusFilter";
import { JobApplication } from "../../../../Entities/JobApplication";
import { Session } from "../../../../Entities/Session";
import interviewService from "../../../../Service/interview.service";
import { openNotification } from "../../../../Components/Notification";
import { useLoader } from "../../../../Context/LoaderContext";

interface JobApplicationinterviewRoundsHeaderProps {
  roundFilter: RoundStatusFilter;
  setRoundFilter: React.Dispatch<React.SetStateAction<RoundStatusFilter>>;
  filterCounts: {
    all: number;
    qualified: number;
    unqualified: number;
    pending: number;
  };
  jobApplication: JobApplication | null;
  setJobApplication: React.Dispatch<
    React.SetStateAction<JobApplication | null>
  >;
  selectedSession: Session | null;
  interviewSessions: Session[];
  setInterviewSessions: React.Dispatch<React.SetStateAction<Session[]>>;
  setActiveTabKey: (key: string) => void;
}

const JobApplicationinterviewRoundsHeader: React.FC<
  JobApplicationinterviewRoundsHeaderProps
> = ({
  roundFilter,
  setRoundFilter,
  filterCounts,
  jobApplication,
  setJobApplication,
  selectedSession,
  interviewSessions,
  setInterviewSessions,
  setActiveTabKey,
}) => {
  const { setLoading, setLoadingMessage } = useLoader();
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [pendingStatus, setPendingStatus] = useState<
    "qualified" | "unqualified" | null
  >(null);

  // Compute overall assessment status based on jobApplication.questionnaireStatus
  const overallStatus = useMemo(() => {
    const status = jobApplication?.questionnaireStatus;

    if (!status) {
      return { label: "No Assessments", color: "default" };
    }

    switch (status) {
      case "qualified":
        return { label: "Qualified", color: "green" };
      case "unqualified":
        return { label: "Unqualified", color: "red" };
      case "pending":
        return { label: "Pending", color: "orange" };
      case "partially_qualified":
        return { label: "Partially Qualified", color: "orange" };
      default:
        return { label: "Unknown", color: "default" };
    }
  }, [jobApplication?.questionnaireStatus]);

  const isPartiallyQualified =
    jobApplication?.questionnaireStatus === "partially_qualified";

  const handleStatusUpdate = (status: "qualified" | "unqualified") => {
    setPendingStatus(status);
    setConfirmModalVisible(true);
  };

  const handleConfirmStatusUpdate = async () => {
    if (!jobApplication || !pendingStatus) {
      return;
    }

    // Use selectedSession if available, otherwise use the first session
    const sessionToUpdate = selectedSession || interviewSessions[0];
    if (!sessionToUpdate) {
      openNotification("error", "Error", "No session found to update");
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage(`Marking as ${pendingStatus}...`);

      const response = await interviewService.updateSessionStatus(
        sessionToUpdate._id,
        pendingStatus
      );

      if (response?.success && response?.data) {
        // Update job application
        if (response.data.jobApplication) {
          setJobApplication(response.data.jobApplication);
        }

        // Update session in the sessions array
        if (response.data.session) {
          setInterviewSessions((prevSessions) =>
            prevSessions.map((session) =>
              session._id === response.data.session._id
                ? response.data.session
                : session
            )
          );
        }

        // Switch to summary tab
        setActiveTabKey("summary");

        openNotification(
          "success",
          "Status Updated",
          response.message || `Application marked as ${pendingStatus}`
        );
      } else {
        openNotification(
          "error",
          "Error",
          response?.message || "Failed to update status"
        );
      }
    } catch (error: any) {
      console.error("Error updating session status:", error);
      openNotification("error", "Error", "Failed to update status");
    } finally {
      setLoading(false);
      setLoadingMessage(null);
      setConfirmModalVisible(false);
      setPendingStatus(null);
    }
  };

  const handleCancelStatusUpdate = () => {
    setConfirmModalVisible(false);
    setPendingStatus(null);
  };

  const menuItems: MenuProps["items"] = [
    {
      key: "qualified",
      label: "Mark as Qualified",
      onClick: () => handleStatusUpdate("qualified"),
    },
    {
      key: "unqualified",
      label: "Mark as Unqualified",
      onClick: () => handleStatusUpdate("unqualified"),
    },
  ];

  return (
    <>
      <div
        className="job-application-interview-rounds-header-container"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "16px",
          justifyContent: "space-between",
        }}
      >
        <div
          className="assessment-header-top"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "8px",
            flex: 1,
          }}
        >
          <Typography.Title level={5} style={{ margin: 0 }}>
            Assessment Summary
          </Typography.Title>
          {!isPartiallyQualified && (
            <Typography.Text type="secondary">
              AI-powered assessment details for this job application
            </Typography.Text>
          )}
          <Space
            direction="horizontal"
            align="center"
            size={8}
            style={{ marginTop: "4px", width: "100%" }}
          >
            {isPartiallyQualified ? (
              <>
                <Typography.Text type="secondary" style={{ fontSize: "14px" }}>
                  <strong>Action Required: </strong> Since this
                  job post is configured with optional qualifying rounds, some
                  of them were failed by the candidate and the assessment has
                  been marked as partially qualified. Please review and update
                  the assessment status.
                </Typography.Text>
                <Dropdown menu={{ items: menuItems }} trigger={["click"]}>
                  <Button
                    type="primary"
                    size="small"
                    style={{ marginLeft: "8px" }}
                  >
                    Update Status <DownOutlined />
                  </Button>
                </Dropdown>
              </>
            ) : (
              <Tag color={overallStatus.color}>
                Status: {overallStatus.label}
              </Tag>
            )}
          </Space>
        </div>

        <AssessmentStatusFilter
          value={roundFilter}
          onChange={setRoundFilter}
          counts={filterCounts}
        />
      </div>

      <Modal
        title="Confirm Status Update"
        open={confirmModalVisible}
        onOk={handleConfirmStatusUpdate}
        onCancel={handleCancelStatusUpdate}
        okText="Confirm"
        cancelText="Cancel"
        centered
        okButtonProps={{
          danger: pendingStatus === "unqualified",
        }}
      >
        <p>
          Are you sure you want to mark this application as{" "}
          <strong>{pendingStatus}</strong>?
        </p>
        <p style={{ color: "#8c8c8c", fontSize: "12px", marginTop: "8px" }}>
          This action will update the questionnaire status and cannot be undone.
        </p>
      </Modal>
    </>
  );
};

export default JobApplicationinterviewRoundsHeader;
